
import React from "react";
import * as Hooks from "../../../hooks";
import { i18nManager } from "../../../i18n";
import { Schemes, Utils } from "../../../utils";
import { Elements } from "../../shared/Elements";
import MediaInput from "../../shared/MediaInput";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog } from "../../shared/ConfirmDialog";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { Chart, Button, ButtonManageRef, Classname, DateHelper, DateInput, DateTimeInput, DateTimePickerLayout, DateTimePickerSelectionMode, Dropdown, FormControl, Scheme, TextInput, ChartType, ChartData, Table, Column, useScreenDimension, StringHelper } from "@rivtn/noseur";
import { ApiResponseData, BaseService, CountryService, ICountry, IMember, IHost, SanitizedResponse, HostRole, HostService, EarningService, IMemberCurrencyBalance, ITransaction, TransactionStatus, TransactionType, TransactionSourceType } from "../../../services";

export interface TransactionsProps {
    profile: IMember;
}

function Transactions(props: TransactionsProps) {

    const { profile } = props;
    const { width } = useScreenDimension();
    const labels = i18nManager.Labels.earnings;
    const earningService = EarningService.getInstance();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [transactions, setTransactions] = React.useState<ApiResponseData<ITransaction> | undefined>({});

    React.useEffect(() => {
        fetchTransactions();
    }, [searchParams]);

    return (<div className="earnings">
        <div className="header">
            <div className="l">
                <span className="t"><i className="fa fa-arrow-left" onClick={() => window.history.back()} /> {labels.earnings} - {labels.transactions.title}</span>
            </div>
            <div className="queries">
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.transactions.type_input.title, }}
                    selectedOptionIndex={(labels.transactions.type_input.options!.findIndex((p) => p?.value === (searchParams.get("type"))) ?? -1) + 1}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={[{ value: "", label: i18nManager.Labels.common.all }, ...labels.transactions.type_input.options!]} onSelectOption={(option) => {
                        Utils.updateSearchParams("type", option.value, setSearchParams);
                    }} />
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.transactions.currency_input.title, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr"))) ?? -1) + 1}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={[{ currency_code: "", currency_name: i18nManager.Labels.common.all }, ...(i18nManager.CountriesAndCurrencies ?? [])]} optionMap={{
                        value: "currency_code",
                        label: "{currency_name} ({currency_code})",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("curr", option.currency_code, setSearchParams);
                    }} />
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={labels.date_range_input.title} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams);
                        }} />
                </FormControl>
            </div>
        </div>
        <div className="transactions">
            <span className="link" onClick={downloadTransactions}>{labels.transactions.export}</span>
            <Table stripedRows scheme={Schemes.OMNEHOST} data={transactions?.content} totalRecords={transactions?.total_elements} dataRefreshKeys={["external_id", "created_at"]}
                style={{ flex: 1, width: "100%" }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE} emptyState={Elements.buildEmptyListView("fa fa-wallet", labels.transactions.no_transaction, { marginTop: 70 })}>
                <Column className="table-c" header={labels.transactions.table.headers.date} dataKey="created_at" template={(v) => Utils.formatToFineDate(v, true)} />
                <Column className="table-c" header={labels.transactions.table.headers.source} template={(v: ITransaction) => {
                    let link = EarningService.buildTransactionSourceLink(v);
                    const label = v.source_data?.name ?? v.source_data?.data?.name;
                    return (<div style={{ display: "flex", flexDirection: "row", alignItems: "center", }}>
                        {v.source_data?.image_location && (<img alt={label} src={v.source_data?.image_location} style={{ width: 20, height: 20, marginRight: 10, borderRadius: "100%" }} />)}
                        {link ? (<a target="_blank" href={link}>{label}</a>) : (<span>{label}</span>)}
                    </div>);
                }} />
                <Column className="table-c" header={labels.transactions.table.headers.description} dataKey="description" />
                <Column className="table-c" header={labels.transactions.table.headers.amount} template={(v: ITransaction) => {
                    const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                    return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.amount)}</span>);
                }} />
                <Column className="table-c" header={labels.transactions.table.headers.platform_charge} template={(v: ITransaction) => {
                    const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                    return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.platform_amount)} ({v.extra_data?.platform_charge_percentage ?? 0} %)</span>);
                }} />
                <Column className="table-c" header={labels.transactions.table.headers.tax} template={(v: ITransaction) => {
                    const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                    return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.tax_amount)} ({v.extra_data?.tax_amount_percentage ?? 0} %)</span>);
                }} />
                <Column className="table-c" header={labels.transactions.table.headers.amount_received} template={(v: ITransaction) => {
                    const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                    return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.payable_amount)}</span>);
                }} />
            </Table>
            <div className="pagination">
                {Elements.buildPaginator(transactions ?? {},
                    ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams),
                    parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
                {(width > 800) && Elements.buildSorter(labels.transactions.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams), labels.transactions.sorts[0].label)}
            </div>
        </div>
    </div>);

    function fetchTransactions() {
        setTransactions(undefined);
        earningService.queryTransactions({
            sort: searchParams.get("sort") ?? "",
            type: searchParams.get("type") ?? "",
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
            currency_code: searchParams.get("curr") ?? "",
            created_at_to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            created_at_from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        }).then(({ apiResponse }: SanitizedResponse<any>) => {
            if (searchParams.has("test")) {
                setTransactions({ content: [] });
                return;
            }
            setTransactions(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function downloadTransactions() {
        const rows = [[labels.transactions.table.headers.date, labels.transactions.table.headers.source, labels.transactions.table.headers.description, labels.transactions.table.headers.amount, labels.transactions.table.headers.platform_charge, labels.transactions.table.headers.tax, labels.transactions.table.headers.amount_received]]
            .concat((transactions?.content ?? []).map((v) => {
                const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;

                return ([
                    Utils.formatToFineDate(v.created_at, true),
                    v.source_data?.name ?? v.source_data?.data?.name ?? "",
                    v.description ?? "",
                    `${isWithdrawal ? "-" : ""}${i18nManager.getCurrencySign(v.currency_code)} ${StringHelper.toPresentableMoneyValue(v.amount)}`,
                    `${isWithdrawal ? "-" : ""}${i18nManager.getCurrencySign(v.currency_code)} ${StringHelper.toPresentableMoneyValue(v.platform_amount)} (${v.extra_data?.platform_charge_percentage ?? 0} %)`,
                    `${isWithdrawal ? "-" : ""}${i18nManager.getCurrencySign(v.currency_code)} ${StringHelper.toPresentableMoneyValue(v.tax_amount)} (${v.extra_data?.tax_amount_percentage ?? 0} %)`,
                    `${isWithdrawal ? "-" : ""}${i18nManager.getCurrencySign(v.currency_code)} ${StringHelper.toPresentableMoneyValue(v.payable_amount)}`
                ]);
            }));
        Utils.downloadBlob(rows.map(row => row.map(String)
            .map((v: any) => v.replaceAll('"', '""'))
            .map((v: any) => `"${v}"`)
            .join(",")
        ).join('\r\n'), `omnehost_transactions_${searchParams.get("from") ?? "2025-02-01 01:00:00 GMT"}_${searchParams.get("to") ?? Utils.formatToApiDate(new Date())}`);
    }

}

export default Transactions;
